import React, { useEffect, useState } from "react";
import analytics from "@react-native-firebase/analytics";
import * as Sentry from "@sentry/react-native";
import * as SecureStore from "expo-secure-store";
import Purchases from "react-native-purchases";

import { isSessionExpired, parseSessionString } from "./helper";

import { configureSentryUser } from "@/utils/sentry";
import {
  type AppUserSession,
  type NullableAppUserSession,
} from "@/schemas/auth/session";
import AuthService from "@/services/internal/auth";
import { isWeb } from "@/utils/platform";
import { NetworkError } from "@/schemas/api/error";
import IdentityService from "@/services/user/identity";
import type { UserIdentity } from "@/schemas/user/identity";
import { getDeviceIdentifier } from "@/hooks/device";

export type SessionContext = {
  signIn: (userSession: AppUserSession) => void;
  signOut: () => void;
  refreshUserIdentity: () => Promise<void>;
  setIdentity: (identity: UserIdentity) => void;
  session: AppUserSession | null;
  isSessionLoading: boolean;
  deviceId: string;
};

export const AuthContext = React.createContext<{
  signIn: (userSession: AppUserSession) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUserIdentity: () => Promise<void>;
  setIdentity: (identity: UserIdentity) => void;
  session: AppUserSession | null;
  isSessionLoading: boolean;
  deviceId: string;
} | null>(null);

export async function getUsernameFromStorage(): Promise<string | null> {
  const sessionString = await SecureStore.getItemAsync("session");
  const localStorageSession = parseSessionString(sessionString);
  if (localStorageSession !== null) {
    return localStorageSession.identity.username;
  }
  return null;
}

// TODO: refactor, this is the same as refreshSession in helper.tsx to avoid circular import
async function refreshSession(
  session: AppUserSession,
): Promise<AppUserSession> {
  const authService = new AuthService();
  return await authService.refreshToken({
    username: session.identity.username,
    refreshToken: session.token.refreshToken,
  });
}

export function SessionProvider(
  properties: React.PropsWithChildren,
): React.JSX.Element {
  const [session, setAppUserSession] = useState<NullableAppUserSession>(null);
  const [isSessionLoading, setIsSessionLoading] = useState<boolean>(true);
  const [deviceId, setDeviceId] = useState<string>("");

  const setSession = async (sess: AppUserSession): Promise<void> => {
    if (isSessionExpired(sess)) {
      sess = await refreshSession(sess);
    }
    setAppUserSession(sess);
    const sessionString = JSON.stringify(sess);
    await SecureStore.setItemAsync("session", sessionString);
  };

  const setIdentity = async (identity: UserIdentity): Promise<void> => {
    if (!session) return;
    await setSession({ ...session, identity });
  };

  const clearSession = async (): Promise<void> => {
    setAppUserSession(null);
    await SecureStore.deleteItemAsync("session");
  };

  const loadDeviceId = async (): Promise<void> => {
    try {
      const { deviceId: id } = await getDeviceIdentifier();
      setDeviceId(id);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const loadSession = async (): Promise<void> => {
    setIsSessionLoading(true);
    const sessionString = await SecureStore.getItemAsync("session");
    try {
      const session = parseSessionString(sessionString);
      if (session !== null) {
        await setSession(session);
      }
    } catch (error) {
      if (error instanceof NetworkError === false) {
        Sentry.captureException(error);
        await clearSession();
      }
    } finally {
      setIsSessionLoading(false);
    }
  };

  useEffect(() => {
    if (!isWeb()) {
      loadDeviceId();
      loadSession();
    }
  }, []);

  const signIn = async (sess: AppUserSession): Promise<void> => {
    try {
      configureSentryUser(sess.identity.username);
      await setSession(sess);
      await Purchases.logIn(sess.identity.username);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      configureSentryUser(null);
      await clearSession();
      await Purchases.logOut();
      await analytics().logEvent("app_sign_out");
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const refreshUserIdentity = async (): Promise<void> => {
    if (!session) return;

    const identityService = new IdentityService(session);
    const updatedIdentity = await identityService.refreshUserIdentity();
    await setIdentity(updatedIdentity);
  };

  return (
    <AuthContext.Provider
      value={{
        isSessionLoading,
        session,
        signIn,
        signOut,
        refreshUserIdentity,
        setIdentity,
        deviceId,
      }}
    >
      {properties.children}
    </AuthContext.Provider>
  );
}
